<template>
  <Teleport to="body">
    <transition name="nd-fade">
      <div v-if="open" class="nd-backdrop">
        <div class="nd-panel">
          <div class="nd-header">
            <div class="nd-title">การแจ้งเตือน</div>
            <div class="nd-header-actions">
              <button
                v-if="unreadCount > 0"
                class="nd-mark-all"
                type="button"
                @click="onMarkAll"
              >
                อ่านทั้งหมด
              </button>
              <button
                class="nd-close"
                type="button"
                aria-label="ปิด"
                @click="$emit('close')"
              >
                <el-icon :size="18"><Close /></el-icon>
              </button>
            </div>
          </div>
          <div v-if="loading && items.length === 0" class="nd-state">
            กำลังโหลด…
          </div>
          <div v-else-if="items.length === 0" class="nd-state">
            ยังไม่มีการแจ้งเตือน
          </div>
          <div v-else class="nd-list">
            <div
              v-for="n in items.slice(0, 5)"
              :key="n.id"
              class="nd-item"
              :class="{
                'nd-unread': !n.readAt,
                [`nd-sev-${n.severity.toLowerCase()}`]: true,
              }"
              @click="onClick(n)"
            >
              <div v-if="!n.readAt" class="nd-dot" />
              <div class="nd-body">
                <div class="nd-row-title">{{ n.title }}</div>
                <div v-if="n.body" class="nd-row-body">{{ n.body }}</div>
                <div class="nd-row-meta">{{ formatRelative(n.createdAt) }}</div>
              </div>
              <button
                class="nd-dismiss"
                type="button"
                aria-label="ปิดการแจ้งเตือน"
                @click.stop="onDismiss(n.id)"
              >
                <el-icon :size="14"><Close /></el-icon>
              </button>
            </div>
          </div>
          <div class="nd-footer" @click="onSeeAll">ดูทั้งหมด ›</div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { onMounted, watch } from "vue";
import { Close } from "@element-plus/icons-vue";
import { useRouter } from "vue-router";
import { useNotifications } from "@/composables/useNotifications";
import type { NotificationView } from "@/api/notification.api";

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ (e: "close"): void }>();

const router = useRouter();
const {
  items,
  loading,
  unreadCount,
  refreshList,
  markRead,
  markAllRead,
  dismiss,
} = useNotifications();

// LIFF Android WebView produces a delayed ghost click after touchend that
// kept landing on a backdrop close handler, snapping the dropdown shut a
// frame after it opened. Earlier we tried @click.self + a 350 ms guard,
// but the timing window in the WebView is unpredictable. Final fix: drop
// the click-to-close on the backdrop entirely. Users close via the ✕ in
// the panel header (mounted inside .nd-panel so any stray ghost click on
// the surrounding backdrop is harmless).
onMounted(() => {
  if (props.open) refreshList();
});

// Refresh list when the dropdown is opened from outside.
watch(
  () => props.open,
  (v) => {
    if (v) refreshList();
  },
);

async function onClick(n: NotificationView): Promise<void> {
  if (!n.readAt) await markRead(n.id);
  emit("close");
  // Deep-link by data.appointmentId when present.
  const apptId = (n.data?.appointmentId as string | undefined) ?? null;
  if (apptId) {
    if (n.type === "APPOINTMENT_BOOKED" || n.type === "APPOINTMENT_REMINDER") {
      router.push({ name: "customer-appointment" });
    } else if (n.type === "APPOINTMENT_CANCELLED") {
      router.push({ name: "customer-appointment" });
    } else if (n.type === "TREATMENT_COMPLETED") {
      router.push({ name: "customer-history" });
    }
  }
}

async function onMarkAll(): Promise<void> {
  await markAllRead();
}

async function onDismiss(id: string): Promise<void> {
  await dismiss(id);
}

function onSeeAll(): void {
  emit("close");
  router.push({ name: "customer-notification" });
}

const RELATIVE_UNITS: [number, string][] = [
  [60_000, "เมื่อสักครู่"],
  [3_600_000, "นาทีที่แล้ว"],
  [86_400_000, "ชั่วโมงที่แล้ว"],
  [2_592_000_000, "วันที่แล้ว"],
];

function formatRelative(iso: string): string {
  const delta = Date.now() - new Date(iso).getTime();
  if (delta < RELATIVE_UNITS[0][0]) return RELATIVE_UNITS[0][1];
  if (delta < RELATIVE_UNITS[1][0])
    return `${Math.floor(delta / 60_000)} ${RELATIVE_UNITS[1][1]}`;
  if (delta < RELATIVE_UNITS[2][0])
    return `${Math.floor(delta / 3_600_000)} ${RELATIVE_UNITS[2][1]}`;
  if (delta < RELATIVE_UNITS[3][0])
    return `${Math.floor(delta / 86_400_000)} ${RELATIVE_UNITS[3][1]}`;
  const d = new Date(iso);
  return d.toLocaleDateString("th-TH");
}
</script>

<style scoped>
.nd-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 9000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 72px;
  padding-inline: 16px;
  /* Backdrop is non-interactive — ghost click after touchend on the bell
     should pass through (or rather, hit nothing) instead of bouncing back
     and forcing the dropdown shut. The panel below opts back in. */
  pointer-events: none;
}
.nd-panel {
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.25);
  max-height: calc(100vh - 96px);
  display: flex;
  flex-direction: column;
  pointer-events: auto; /* re-enable for the panel itself */
}
.nd-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 0.5px solid #e5eaf2;
}
.nd-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--c-ink);
}
.nd-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.nd-mark-all {
  background: transparent;
  border: none;
  color: var(--c-primary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
}
.nd-mark-all:active {
  background: #e8f0fe;
}
.nd-close {
  background: transparent;
  border: none;
  color: var(--c-ink-soft);
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.nd-close:active {
  background: #f0f2f7;
  color: var(--c-ink);
}
.nd-state {
  padding: 36px 16px;
  text-align: center;
  font-size: 13px;
  color: var(--c-ink-soft);
}
.nd-list {
  overflow-y: auto;
  flex: 1;
}
.nd-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 0.5px solid #f0f2f7;
  cursor: pointer;
  background: #ffffff;
  transition: background 0.12s;
}
.nd-item:hover {
  background: #f8fafc;
}
.nd-item:last-child {
  border-bottom: none;
}
.nd-item.nd-unread {
  background: #f0f6ff;
}
.nd-item.nd-unread:hover {
  background: #e8f0fe;
}
.nd-dot {
  margin-top: 7px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--c-primary);
  flex-shrink: 0;
}
.nd-sev-warning .nd-dot {
  background: #d97706;
}
.nd-sev-urgent .nd-dot {
  background: #dc2626;
}
.nd-body {
  flex: 1;
  min-width: 0;
}
.nd-row-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--c-ink);
}
.nd-row-body {
  font-size: 12px;
  color: var(--c-ink-soft);
  margin-top: 3px;
  word-break: break-word;
}
.nd-row-meta {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
}
.nd-dismiss {
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  flex-shrink: 0;
}
.nd-dismiss:hover {
  background: #e2e8f0;
  color: var(--c-ink);
}

.nd-footer {
  text-align: center;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--c-primary);
  cursor: pointer;
  border-top: 0.5px solid #e5eaf2;
  background: #ffffff;
}
.nd-footer:hover {
  background: #f0f6ff;
}

.nd-fade-enter-active,
.nd-fade-leave-active {
  transition: opacity 0.18s ease;
}
.nd-fade-enter-from,
.nd-fade-leave-to {
  opacity: 0;
}
</style>
